// @ts-check

// Vendor imports
const _ = require('lodash')
// Client lib imports
const { localStorage } = require('client/lib/storage')

/** @typedef { import('./types.d').PageContext } PageContext */
/** @typedef { import('./types.d').PageContextWithReferrer } PageContextWithReferrer */
/** @typedef { import('./types.d').DenormalizedPageContext } DenormalizedPageContext */

const REFERRER_PREFIX = 'referrer_'
const STREAM_FIELD_KEYS = ['stream_type', 'stream_id', 'stream_key']

const PERSISTENCE_KEY = 'pageContextPersistence'
const PERSISTENCE_TTL = 30000

/** @type {PageContext} */
let currentPageContext
/** @type {PageContext} */
let referrerPageContext

/**
 * @returns {PageContext}
 */
function getCurrentPageContext() {
  return _.clone(currentPageContext || {})
}

/**
 * @returns {PageContext}
 */
function getReferrerPageContext() {
  return _.clone(referrerPageContext || {})
}

/**
 * Get a copy of the current global page context
 * @returns {PageContextWithReferrer}
 */
function getPageContext() {
  /** @type {PageContextWithReferrer} */
  const pageContext = getCurrentPageContext()
  if (referrerPageContext) {
    _.each(referrerPageContext, (value, key) => {
      // @ts-ignore TypeScript can't figure out that this is still type-conformant.
      pageContext[REFERRER_PREFIX + key] = value
    })
  }
  return pageContext
}

/**
 * Replace the `referrerPageContext` with the `currentPageContext` and
 * set the `currentPageContext` to the `newPageContext`
 * @param {DenormalizedPageContext} newPageContext
 * @param {boolean} [persistStreamContext]
 */
function setPageContext(newPageContext, persistStreamContext) {
  let normalizedPageContext = normalizePageContext(newPageContext)
  if (!referrerPageContext) {
    referrerPageContext = getPersistedPageContext()
  }
  if (currentPageContext) {
    referrerPageContext = _.clone(currentPageContext)
    // Typically, when we jump from a stream (ex: mix profile) to card view,
    // the stream_id and stream_type should stay unchanged, but the page_name should change.
    if (persistStreamContext) {
      const currentStream = _.pick(currentPageContext, STREAM_FIELD_KEYS)
      normalizedPageContext = _.merge(currentStream, normalizedPageContext)
    }
  }
  currentPageContext = normalizedPageContext
}

/**
 * Overwrite the current and referrer global page contexts.
 * Used by `withTemporaryPageContext` to restore the stashed context.
 * @param {PageContextWithReferrer} pageContextWithReferrer
 */
function revertPageContext(pageContextWithReferrer) {
  currentPageContext = unpackCurrentPageContext(pageContextWithReferrer)
  referrerPageContext = unpackReferrerPageContext(pageContextWithReferrer)
}

/**
 * 1. Stash the current and referrer global page context
 * 2. Set the new current context to `temporaryPageContext`
 * 3. Run a callback in the temporary global page context
 * 4. Restore the current and referrer global page context from the stash
 * @param {DenormalizedPageContext} temporaryPageContext
 * @param {function(): void} callback
 */
function withTemporaryPageContext(temporaryPageContext, callback) {
  if (!temporaryPageContext) {
    callback()
    return
  }
  const currentPageContext = getPageContext()
  setPageContext(temporaryPageContext)
  callback()
  revertPageContext(currentPageContext)
}

/**
 * Store an overlaid copy of the current page context in localStorage.
 * This is used to propagate page context during full-page navigation and opening new windows.
 * @param {DenormalizedPageContext} [pageContextOverlay]
 */
function persistPageContext(pageContextOverlay = {}) {
  localStorage.removeItem(PERSISTENCE_KEY)
  const persistencePageContext = _.merge(
    {},
    currentPageContext,
    normalizePageContext(pageContextOverlay)
  )
  if (!_.isEmpty(persistencePageContext)) {
    const persistenceValue = JSON.stringify({
      pageContext: persistencePageContext,
      ttl: Date.now() + PERSISTENCE_TTL
    })
    localStorage.setItem(PERSISTENCE_KEY, persistenceValue)
  }
}

/**
 * Retrieve the stored page context from localStorage.
 * This is used after full-page navigation to determine referrer page context
 * @returns {PageContext}
 */
function getPersistedPageContext() {
  const persistenceValue = localStorage.getItem(PERSISTENCE_KEY)
  localStorage.removeItem(PERSISTENCE_KEY) // Burn after reading
  if (persistenceValue) {
    const persistenceObj = JSON.parse(persistenceValue)
    if (persistenceObj.ttl > Date.now()) {
      return persistenceObj.pageContext
    }
  }
}

/**
 * Convert camelCased page context into snake_case page context.
 * The back-end is expecting snake_case, but camelCase is more convenient to use in JavaScript.
 * @param {DenormalizedPageContext} pageContext
 * @returns {PageContext}
 */
function normalizePageContext(pageContext) {
  return _.transform(pageContext, (acc, value, key) => {
    const normalizedKey = _.snakeCase(key)
    if (value !== undefined) {
      // @ts-ignore TypeScript can't figure out that this is still type-conformant.
      acc[normalizedKey] = value
    }
  })
}

/**
 * @param {PageContextWithReferrer} pageContextWithReferrer
 * @returns {PageContext}
 */
function unpackCurrentPageContext(pageContextWithReferrer) {
  return _.transform(pageContextWithReferrer, (acc, value, key) => {
    if (!key.startsWith(REFERRER_PREFIX) && key !== '__keep') {
      // @ts-ignore TypeScript can't figure out that this is still type-conformant.
      acc[key] = value
    }
  })
}

/**
 * @param {PageContextWithReferrer} pageContextWithReferrer
 * @returns {PageContext}
 */
function unpackReferrerPageContext(pageContextWithReferrer) {
  return _.transform(pageContextWithReferrer, (acc, value, key) => {
    if (key.startsWith(REFERRER_PREFIX)) {
      // @ts-ignore TypeScript can't figure out that this is still type-conformant.
      acc[key.slice(REFERRER_PREFIX.length)] = value
    }
  })
}

module.exports = {
  getCurrentPageContext,
  getReferrerPageContext,
  getPageContext,
  getPersistedPageContext,
  normalizePageContext,
  persistPageContext,
  revertPageContext,
  setPageContext,
  unpackCurrentPageContext,
  unpackReferrerPageContext,
  withTemporaryPageContext
}
